/**
 * Admin bonus manager — `/admin/bonuses` (Phase 2, 2026-02).
 *
 * Views:
 *   - LIST     : table of every bonus (create/edit/view-claims/soft-delete)
 *   - EDITOR   : full form for `deposit_match | cashback | game_free_credit`
 *   - CLAIMS   : modal listing claims for one bonus with force-forfeit
 *   - GRANT    : small form to grant a bonus to a specific user by id
 *
 * Every submit lives in a local busy-flag so we never fire duplicate PATCHes
 * or double-claims. The list refreshes after every mutation.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import {
  Gift, Plus, Trash2, Users, Edit3, Power, PowerOff, RefreshCw, X,
  Ticket, Repeat,
} from "lucide-react";

import api from "../../lib/api";
import { extractError } from "../../lib/formatError";
import { Badge } from "../../components/ui-r1/Badge";
import Button from "../../components/ui-r1/Button";
import { Card, CardBody } from "../../components/ui-r1/Card";
import { CURRENCIES, formatAmount } from "../../lib/currency";
import { BONUS_TYPE_LABEL, BONUS_TYPE_TONE, CLAIM_STATUS_LABEL, CLAIM_STATUS_TONE, progressPct } from "../../lib/bonusHelpers";

const GAME_SLUGS = ["bra-3-way", "bra-2-way", "sexy-tattoo", "hot-bikini", "scratch-card"];

const EMPTY_FORM = {
  slug: "",
  name: "",
  description: "",
  bonus_type: "deposit_match",
  enabled: true,
  config: {
    match_percent: 100,
    max_bonus: { BDT: 500, INR: 500, PKR: 500, USDT: 5 },
    min_deposit: { BDT: 100, INR: 100, PKR: 100, USDT: 1 },
    deposit_method_whitelist: ["nagad"],
    cashback_percent: 10,
    cycle: "daily",
    min_net_loss: { BDT: 100, INR: 100, PKR: 100, USDT: 1 },
    max_cashback: { BDT: 1000, INR: 1000, PKR: 1000, USDT: 10 },
    free_credit: { BDT: 20, INR: 20, PKR: 20, USDT: 0.5 },
    eligible_game_slugs: ["scratch-card"],
    one_claim_per_user: true,
  },
  targeting: {
    currencies: ["BDT", "USDT"],
    geo_allowlist: null,
    geo_blocklist: ["US"],
    user_segment: "all",
    start_at: new Date().toISOString(),
    end_at: null,
    promo_code: null,
  },
  wagering: {
    multiplier: 30,
    max_bet: { BDT: 100, INR: 100, PKR: 100, USDT: 1 },
    expiry_days: 7,
    game_weights: { "bra-3-way": 100, "bra-2-way": 100, "sexy-tattoo": 100, "hot-bikini": 100, "scratch-card": 50 },
  },
};

const ICON_FOR_TYPE = {
  deposit_match: Gift,
  cashback: Repeat,
  game_free_credit: Ticket,
};

export default function BonusManager() {
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState({ mode: "list" });
  const [claimsFor, setClaimsFor] = useState(null); // bonus opened in claims modal
  const [grantOpen, setGrantOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bonuses?include_deleted=false");
      setBonuses(data.items || []);
      setError(null);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleToggle = async (b) => {
    try {
      await api.post(`/admin/bonuses/${b.id}/toggle`);
      toast.success(`${b.slug} → ${b.enabled ? "disabled" : "enabled"}`);
      reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Soft-delete "${b.name}"? It will hide from lists but stay in the audit log.`)) return;
    try {
      await api.delete(`/admin/bonuses/${b.id}`);
      toast.success(`${b.slug} soft-deleted`);
      reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const totals = useMemo(() => {
    const t = { total: bonuses.length, enabled: 0, claims: 0, awarded: 0, completed: 0, forfeited: 0 };
    for (const b of bonuses) {
      if (b.enabled) t.enabled += 1;
      const s = b.stats_cache || {};
      t.claims += s.claim_count || 0;
      t.awarded += s.total_awarded_usd_equiv || 0;
      t.completed += s.completed_wagering_count || 0;
      t.forfeited += s.forfeited_count || 0;
    }
    return t;
  }, [bonuses]);

  return (
    <div className="max-w-[1440px] mx-auto space-y-6" data-testid="admin-bonus-manager">
      <Toaster theme="dark" position="top-center" />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-1">Bonuses</div>
          <h1 className="font-display italic text-3xl sm:text-4xl text-ink">Bonus manager</h1>
          <p className="text-xs text-ink-2 mt-1">Create, edit, and audit all live bonus offers.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="md" onClick={reload} data-testid="admin-bonuses-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="ghost" size="md" onClick={() => setGrantOpen(true)} data-testid="admin-bonuses-grant-btn">
            <Users className="w-3.5 h-3.5 mr-1.5" />
            Grant to user
          </Button>
          <Button variant="pink" size="md" onClick={() => setView({ mode: "create" })} data-testid="admin-bonuses-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            New bonus
          </Button>
        </div>
      </div>

      {/* Analytics strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat testid="stat-total" label="Bonuses" value={totals.total} />
        <Stat testid="stat-enabled" label="Enabled" value={totals.enabled} tone="hot" />
        <Stat testid="stat-claims" label="Total claims" value={totals.claims} tone="gold" />
        <Stat testid="stat-completed" label="Completed" value={totals.completed} tone="success" />
        <Stat testid="stat-forfeited" label="Forfeited" value={totals.forfeited} tone="danger" />
        <Stat testid="stat-awarded" label="Awarded (usd-eq)" value={`$${totals.awarded.toFixed(2)}`} tone="muted" />
      </div>

      {error && (
        <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2" data-testid="admin-bonuses-error">
          {error}
        </div>
      )}

      {/* List / editor */}
      {view.mode === "list" ? (
        <BonusList
          bonuses={bonuses}
          onEdit={(b) => setView({ mode: "edit", bonus: b })}
          onClaims={(b) => setClaimsFor(b)}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />
      ) : (
        <BonusEditor
          initial={view.mode === "edit" ? view.bonus : null}
          onSaved={() => { setView({ mode: "list" }); reload(); }}
          onCancel={() => setView({ mode: "list" })}
        />
      )}

      {claimsFor && (
        <ClaimsModal
          bonus={claimsFor}
          onClose={() => { setClaimsFor(null); reload(); }}
        />
      )}
      {grantOpen && (
        <GrantModal
          bonuses={bonuses}
          onClose={() => { setGrantOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

function Stat({ testid, label, value, tone = "muted" }) {
  const toneColor = {
    hot: "text-pink",
    gold: "text-gold",
    success: "text-success",
    danger: "text-danger",
    muted: "text-ink",
  }[tone];
  return (
    <div className="rounded-xl border border-line bg-elevated p-3" data-testid={testid}>
      <div className="text-[9px] tracking-widest uppercase text-ink-3">{label}</div>
      <div className={`font-display italic text-2xl mt-1 ${toneColor}`}>{value}</div>
    </div>
  );
}

/* --------------------------------------------------------------- LIST -- */

function BonusList({ bonuses, onEdit, onClaims, onToggle, onDelete }) {
  if (bonuses.length === 0) {
    return (
      <Card>
        <CardBody className="py-10 text-center">
          <Gift className="w-8 h-8 mx-auto text-ink-3 mb-2" />
          <div className="font-display italic text-lg text-ink">No bonuses yet</div>
          <div className="text-xs text-ink-2 mt-1">Hit &quot;New bonus&quot; up top to create your first offer.</div>
        </CardBody>
      </Card>
    );
  }
  return (
    <div className="rounded-xl border border-line bg-elevated overflow-hidden" data-testid="admin-bonus-list">
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] tracking-widest uppercase text-ink-3 bg-highest/50 border-b border-line">
        <div className="col-span-4">Slug / Name</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-2">Claims</div>
        <div className="col-span-1">Completion</div>
        <div className="col-span-1">State</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {bonuses.map((b) => {
        const s = b.stats_cache || {};
        const Icon = ICON_FOR_TYPE[b.bonus_type] || Gift;
        const claims = s.claim_count || 0;
        const completed = s.completed_wagering_count || 0;
        const pct = claims > 0 ? Math.round((completed / claims) * 100) : 0;
        return (
          <div
            key={b.id}
            className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-t border-line text-sm"
            data-testid={`admin-bonus-row-${b.slug}`}
          >
            <div className="col-span-4 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-8 h-8 rounded-lg bg-base border border-line flex items-center justify-center text-pink shrink-0">
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0">
                  <div className="text-ink truncate">{b.name}</div>
                  <div className="text-[10px] text-ink-3 font-mono truncate">{b.slug}</div>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Badge variant={BONUS_TYPE_TONE[b.bonus_type] || "muted"}>{BONUS_TYPE_LABEL[b.bonus_type] || b.bonus_type}</Badge>
            </div>
            <div className="col-span-2 text-ink font-mono text-xs">{claims}</div>
            <div className="col-span-1 text-gold font-mono text-xs">{pct}%</div>
            <div className="col-span-1">
              <span className={"inline-flex px-2 py-0.5 rounded text-[9px] tracking-widest uppercase " + (b.enabled ? "text-success bg-success/10 border border-success/40" : "text-ink-3 bg-highest border border-line")}>
                {b.enabled ? "Live" : "Off"}
              </span>
            </div>
            <div className="col-span-2 flex justify-end gap-1">
              <IconBtn title="Edit" onClick={() => onEdit(b)} testid={`admin-bonus-edit-${b.slug}`}><Edit3 className="w-3.5 h-3.5" /></IconBtn>
              <IconBtn title="View claims" onClick={() => onClaims(b)} testid={`admin-bonus-claims-${b.slug}`}><Users className="w-3.5 h-3.5" /></IconBtn>
              <IconBtn title={b.enabled ? "Disable" : "Enable"} onClick={() => onToggle(b)} testid={`admin-bonus-toggle-${b.slug}`}>
                {b.enabled ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
              </IconBtn>
              <IconBtn title="Soft-delete" tone="danger" onClick={() => onDelete(b)} testid={`admin-bonus-delete-${b.slug}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function IconBtn({ children, onClick, title, tone, testid }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={
        "w-8 h-8 rounded-md border border-line flex items-center justify-center transition " +
        (tone === "danger"
          ? "text-danger hover:bg-danger/10 hover:border-danger/60"
          : "text-ink-2 hover:text-ink hover:bg-highest")
      }
      data-testid={testid}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------- EDITOR - */

function BonusEditor({ initial, onSaved, onCancel }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => (initial ? mergeIntoForm(initial) : EMPTY_FORM));
  const [busy, setBusy] = useState(false);

  const patch = (patch) => setForm((f) => ({ ...f, ...patch }));
  const patchConfig = (patch) => setForm((f) => ({ ...f, config: { ...f.config, ...patch } }));
  const patchTargeting = (patch) => setForm((f) => ({ ...f, targeting: { ...f.targeting, ...patch } }));
  const patchWagering = (patch) => setForm((f) => ({ ...f, wagering: { ...f.wagering, ...patch } }));

  const submit = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/bonuses", buildPayload(form, true));
        toast.success("Bonus created");
      } else {
        await api.patch(`/admin/bonuses/${initial.id}`, buildPayload(form, false));
        toast.success("Bonus updated");
      }
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-bonus-editor">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink flex items-center gap-1" data-testid="admin-bonus-editor-back">
          ← Back to list
        </button>
        <div className="text-[10px] tracking-[0.3em] uppercase text-gold">{isNew ? "New bonus" : "Edit bonus"}</div>
      </div>

      {/* Basics */}
      <Card>
        <CardBody className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Slug" required>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => patch({ slug: e.target.value })}
              disabled={!isNew}
              className="w-full bg-base border border-line rounded-lg px-3 py-2 text-sm text-ink focus:border-pink focus:outline-none disabled:opacity-50 font-mono"
              placeholder="welcome_100_bdt"
              data-testid="admin-bonus-form-slug"
            />
          </Field>
          <Field label="Enabled">
            <div className="flex items-center gap-2 h-9">
              <input
                type="checkbox"
                checked={!!form.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
                className="w-4 h-4"
                data-testid="admin-bonus-form-enabled"
              />
              <span className="text-xs text-ink-2">Live for eligibility checks</span>
            </div>
          </Field>
          <Field label="Name" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
              className="w-full bg-base border border-line rounded-lg px-3 py-2 text-sm text-ink focus:border-pink focus:outline-none"
              placeholder="100% Welcome Bonus"
              data-testid="admin-bonus-form-name"
            />
          </Field>
          <Field label="Type" required>
            <select
              value={form.bonus_type}
              onChange={(e) => patch({ bonus_type: e.target.value })}
              disabled={!isNew}
              className="w-full bg-base border border-line rounded-lg px-3 py-2 text-sm text-ink focus:border-pink focus:outline-none disabled:opacity-50"
              data-testid="admin-bonus-form-type"
            >
              <option value="deposit_match">Deposit match</option>
              <option value="cashback">Cashback</option>
              <option value="game_free_credit">Game free credit</option>
            </select>
          </Field>
          <Field label="Description" required span={2}>
            <textarea
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              rows={2}
              className="w-full bg-base border border-line rounded-lg px-3 py-2 text-sm text-ink focus:border-pink focus:outline-none"
              placeholder="Get 100% match on your first Nagad deposit up to ৳500. 30× wagering."
              data-testid="admin-bonus-form-description"
            />
          </Field>
        </CardBody>
      </Card>

      {/* Config sub-form */}
      <Card>
        <CardBody className="space-y-3">
          <div className="text-[10px] tracking-widest uppercase text-gold mb-1">Bonus config</div>
          {form.bonus_type === "deposit_match" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Match %">
                <input type="number" min={0} max={500} value={form.config.match_percent ?? ""} onChange={(e) => patchConfig({ match_percent: Number(e.target.value) })} className="wt-input" data-testid="admin-bonus-form-match-pct" />
              </Field>
              <Field label="Deposit method whitelist (comma-separated)">
                <input type="text" value={(form.config.deposit_method_whitelist || []).join(",")} onChange={(e) => patchConfig({ deposit_method_whitelist: splitCsv(e.target.value) })} className="wt-input" placeholder="nagad,crypto" data-testid="admin-bonus-form-methods" />
              </Field>
              <PerCurrencyField label="Max bonus" value={form.config.max_bonus || {}} onChange={(v) => patchConfig({ max_bonus: v })} testidPrefix="admin-bonus-form-maxbonus" />
              <PerCurrencyField label="Min deposit" value={form.config.min_deposit || {}} onChange={(v) => patchConfig({ min_deposit: v })} testidPrefix="admin-bonus-form-mindep" />
            </div>
          )}
          {form.bonus_type === "cashback" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Cashback %">
                <input type="number" min={0} max={100} value={form.config.cashback_percent ?? ""} onChange={(e) => patchConfig({ cashback_percent: Number(e.target.value) })} className="wt-input" data-testid="admin-bonus-form-cashback-pct" />
              </Field>
              <Field label="Cycle">
                <select value={form.config.cycle || "daily"} onChange={(e) => patchConfig({ cycle: e.target.value })} className="wt-input" data-testid="admin-bonus-form-cycle">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </Field>
              <PerCurrencyField label="Min net loss" value={form.config.min_net_loss || {}} onChange={(v) => patchConfig({ min_net_loss: v })} testidPrefix="admin-bonus-form-minloss" />
              <PerCurrencyField label="Max cashback" value={form.config.max_cashback || {}} onChange={(v) => patchConfig({ max_cashback: v })} testidPrefix="admin-bonus-form-maxcb" />
            </div>
          )}
          {form.bonus_type === "game_free_credit" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PerCurrencyField label="Free credit" value={form.config.free_credit || {}} onChange={(v) => patchConfig({ free_credit: v })} testidPrefix="admin-bonus-form-freecredit" />
              <Field label="Eligible games (comma-separated slugs)">
                <input type="text" value={(form.config.eligible_game_slugs || []).join(",")} onChange={(e) => patchConfig({ eligible_game_slugs: splitCsv(e.target.value) })} className="wt-input" placeholder="scratch-card,bra-2-way" data-testid="admin-bonus-form-eligible-games" />
              </Field>
              <Field label="One claim per user">
                <div className="flex items-center gap-2 h-9">
                  <input type="checkbox" checked={!!form.config.one_claim_per_user} onChange={(e) => patchConfig({ one_claim_per_user: e.target.checked })} data-testid="admin-bonus-form-one-claim" />
                  <span className="text-xs text-ink-2">Prevent multiple claims from same user</span>
                </div>
              </Field>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Targeting */}
      <Card>
        <CardBody className="space-y-3">
          <div className="text-[10px] tracking-widest uppercase text-gold mb-1">Targeting</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Currencies (leave empty for all)">
              <CurrencyChipMulti value={form.targeting.currencies} onChange={(v) => patchTargeting({ currencies: v })} testidPrefix="admin-bonus-form-target-cur" />
            </Field>
            <Field label="User segment">
              <select value={form.targeting.user_segment || "all"} onChange={(e) => patchTargeting({ user_segment: e.target.value })} className="wt-input" data-testid="admin-bonus-form-segment">
                <option value="all">All players</option>
                <option value="new">New only (no deposits yet)</option>
                <option value="kyc_verified">KYC verified only</option>
              </select>
            </Field>
            <Field label="Geo allowlist (ISO codes, CSV, empty = all)">
              <input type="text" value={(form.targeting.geo_allowlist || []).join(",")} onChange={(e) => patchTargeting({ geo_allowlist: splitCsv(e.target.value) || null })} className="wt-input" placeholder="BD,IN,PK" data-testid="admin-bonus-form-geo-allow" />
            </Field>
            <Field label="Geo blocklist (ISO codes, CSV)">
              <input type="text" value={(form.targeting.geo_blocklist || []).join(",")} onChange={(e) => patchTargeting({ geo_blocklist: splitCsv(e.target.value) })} className="wt-input" placeholder="US,GB" data-testid="admin-bonus-form-geo-block" />
            </Field>
            <Field label="Starts at (ISO)">
              <input type="datetime-local" value={toLocalDT(form.targeting.start_at)} onChange={(e) => patchTargeting({ start_at: fromLocalDT(e.target.value) })} className="wt-input" data-testid="admin-bonus-form-start" />
            </Field>
            <Field label="Ends at (ISO — blank = no expiry)">
              <input type="datetime-local" value={toLocalDT(form.targeting.end_at)} onChange={(e) => patchTargeting({ end_at: fromLocalDT(e.target.value) })} className="wt-input" data-testid="admin-bonus-form-end" />
            </Field>
            <Field label="Promo code (blank = none)" span={2}>
              <input type="text" value={form.targeting.promo_code || ""} onChange={(e) => patchTargeting({ promo_code: e.target.value || null })} className="wt-input" placeholder="WELCOME100" data-testid="admin-bonus-form-promo-code" />
            </Field>
          </div>
        </CardBody>
      </Card>

      {/* Wagering */}
      <Card>
        <CardBody className="space-y-3">
          <div className="text-[10px] tracking-widest uppercase text-gold mb-1">Wagering</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Multiplier">
              <input type="number" min={0} value={form.wagering.multiplier ?? ""} onChange={(e) => patchWagering({ multiplier: Number(e.target.value) })} className="wt-input" data-testid="admin-bonus-form-multiplier" />
            </Field>
            <Field label="Expiry (days)">
              <input type="number" min={1} value={form.wagering.expiry_days ?? ""} onChange={(e) => patchWagering({ expiry_days: Number(e.target.value) })} className="wt-input" data-testid="admin-bonus-form-expiry" />
            </Field>
            <div />
            <PerCurrencyField label="Max bet during wager" value={form.wagering.max_bet || {}} onChange={(v) => patchWagering({ max_bet: v })} testidPrefix="admin-bonus-form-maxbet" />
          </div>
          <div>
            <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-1.5">Game weights (% contribution to wagering)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
              {GAME_SLUGS.map((slug) => (
                <div key={slug} className="flex items-center gap-2">
                  <div className="text-[10px] font-mono text-ink-2 flex-1 truncate">{slug}</div>
                  <input
                    type="number"
                    value={form.wagering.game_weights?.[slug] ?? 100}
                    onChange={(e) => patchWagering({ game_weights: { ...(form.wagering.game_weights || {}), [slug]: Number(e.target.value) } })}
                    className="w-20 wt-input"
                    data-testid={`admin-bonus-form-weight-${slug}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="md" onClick={onCancel} data-testid="admin-bonus-form-cancel">Cancel</Button>
        <Button variant="pink" size="md" loading={busy} onClick={submit} data-testid="admin-bonus-form-submit">
          {isNew ? "Create bonus" : "Save changes"}
        </Button>
      </div>

      {/* Ambient global input style so the local `wt-input` shortcut renders consistently. */}
      <style>{`
        .wt-input { width:100%; background: var(--base); border:1px solid var(--line); border-radius:0.5rem; padding:.5rem .75rem; font-size:.875rem; color: var(--ink); }
        .wt-input:focus { border-color: var(--pink); outline:none; }
      `}</style>
    </div>
  );
}

function Field({ label, children, required, span }) {
  return (
    <label className={"block" + (span === 2 ? " md:col-span-2" : "")}>
      <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-1.5">{label}{required && <span className="text-pink"> *</span>}</div>
      {children}
    </label>
  );
}

function PerCurrencyField({ label, value, onChange, testidPrefix }) {
  return (
    <Field label={label}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {CURRENCIES.map((c) => (
          <div key={c.code} className="flex items-center gap-1">
            <span className="text-[10px] text-ink-3 w-8">{c.code}</span>
            <input
              type="number"
              value={value?.[c.code] ?? 0}
              onChange={(e) => onChange({ ...value, [c.code]: Number(e.target.value) })}
              className="wt-input w-full"
              data-testid={`${testidPrefix}-${c.code}`}
            />
          </div>
        ))}
      </div>
    </Field>
  );
}

function CurrencyChipMulti({ value, onChange, testidPrefix }) {
  const val = value || [];
  const toggle = (c) => {
    if (val.includes(c)) onChange(val.filter((x) => x !== c));
    else onChange([...val, c]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {CURRENCIES.map((c) => (
        <button
          key={c.code}
          type="button"
          onClick={() => toggle(c.code)}
          className={
            "px-3 py-1.5 rounded-md text-[10px] tracking-widest uppercase border transition " +
            (val.includes(c.code)
              ? "border-pink text-pink bg-pink/10"
              : "border-line text-ink-2 hover:border-ink-3")
          }
          data-testid={`${testidPrefix}-${c.code}`}
        >
          {c.code}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- CLAIMS -- */

function ClaimsModal({ bonus, onClose }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bonuses/${bonus.id}/claims?limit=100`);
      setClaims(data.items || []);
      setError(null);
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [bonus.id]);

  useEffect(() => { load(); }, [load]);

  const forfeit = async (c) => {
    if (!window.confirm(`Force-forfeit this claim? Bonus balance for ${c.currency} will be zeroed.`)) return;
    setBusy(c.claim_id);
    try {
      await api.post(`/admin/bonuses/claims/${c.claim_id}/forfeit`);
      toast.success("Claim forfeited");
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="admin-claims-modal">
      <div className="w-full max-w-4xl rounded-2xl border border-line bg-elevated overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-gold">Claims</div>
            <div className="font-display italic text-xl text-ink">{bonus.name}</div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" data-testid="admin-claims-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {error && <div className="text-xs text-danger p-4">{error}</div>}
          {loading && claims.length === 0 ? (
            <div className="p-10 text-center text-ink-3 text-sm">Loading…</div>
          ) : claims.length === 0 ? (
            <div className="p-10 text-center text-ink-3 text-sm">No claims yet.</div>
          ) : (
            <div>
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] tracking-widest uppercase text-ink-3 bg-highest/50 border-b border-line">
                <div className="col-span-4">User / Claim ID</div>
                <div className="col-span-1">Cur</div>
                <div className="col-span-2">Awarded</div>
                <div className="col-span-2">Wager progress</div>
                <div className="col-span-1">State</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              {claims.map((c) => (
                <div key={c.claim_id} className="grid grid-cols-12 gap-2 px-4 py-2 border-t border-line items-center text-xs" data-testid={`admin-claims-row-${c.claim_id}`}>
                  <div className="col-span-4 min-w-0">
                    <div className="text-ink font-mono truncate">{c.user_id}</div>
                    <div className="text-[9px] text-ink-3 font-mono truncate">{c.claim_id}</div>
                  </div>
                  <div className="col-span-1 text-ink-2">{c.currency}</div>
                  <div className="col-span-2 gold-text font-mono">{formatAmount(c.amount_awarded, c.currency)}</div>
                  <div className="col-span-2">
                    <div className="text-[10px] text-ink-2 font-mono">
                      {formatAmount(c.wagering_completed, c.currency)} / {formatAmount(c.wagering_required, c.currency)}
                    </div>
                    <div className="h-1 bg-highest rounded overflow-hidden mt-1">
                      <div className="h-full bg-gradient-to-r from-pink to-gold" style={{ width: `${progressPct(c)}%` }} />
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Badge variant={CLAIM_STATUS_TONE[c.status] || "muted"}>{CLAIM_STATUS_LABEL[c.status]}</Badge>
                  </div>
                  <div className="col-span-2 text-right">
                    {(c.status === "pending" || c.status === "active") ? (
                      <button
                        onClick={() => forfeit(c)}
                        disabled={busy === c.claim_id}
                        className="text-[10px] tracking-widest uppercase text-danger hover:bg-danger/10 border border-danger/40 rounded-md px-2 py-1 disabled:opacity-40"
                        data-testid={`admin-claims-forfeit-${c.claim_id}`}
                      >
                        {busy === c.claim_id ? "…" : "Forfeit"}
                      </button>
                    ) : (
                      <span className="text-ink-3 text-[10px]">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- GRANT -- */

function GrantModal({ bonuses, onClose }) {
  const [userId, setUserId] = useState("");
  const [bonusSlug, setBonusSlug] = useState(bonuses[0]?.slug || "");
  const [currency, setCurrency] = useState("BDT");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { user_id: userId.trim(), bonus_slug: bonusSlug, currency };
      const amt = Number(amount);
      if (!Number.isNaN(amt) && amt > 0) payload.amount = amt;
      const { data } = await api.post("/admin/bonuses/grant", payload);
      toast.success(`Granted ${formatAmount(data.amount_awarded, currency)} → claim ${data.claim_id.slice(0, 8)}…`);
      onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="admin-grant-modal">
      <div className="w-full max-w-md rounded-2xl border border-line bg-elevated p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-ink-3 hover:text-ink" data-testid="admin-grant-modal-close">
          <X className="w-4 h-4" />
        </button>
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-1">Grant</div>
        <h3 className="font-display italic text-2xl text-ink mb-5">Award bonus to user</h3>
        <div className="space-y-3">
          <Field label="User ID (uuid)">
            <input type="text" value={userId} onChange={(e) => setUserId(e.target.value)} className="wt-input" placeholder="8a504…6481" data-testid="admin-grant-user-id" />
          </Field>
          <Field label="Bonus">
            <select value={bonusSlug} onChange={(e) => setBonusSlug(e.target.value)} className="wt-input" data-testid="admin-grant-bonus-slug">
              {bonuses.map((b) => <option key={b.slug} value={b.slug}>{b.slug}</option>)}
            </select>
          </Field>
          <Field label="Currency">
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="wt-input" data-testid="admin-grant-currency">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </Field>
          <Field label="Amount (blank = bonus default)">
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="wt-input" placeholder="Auto" data-testid="admin-grant-amount" />
          </Field>
        </div>
        <div className="flex gap-2 mt-5">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="pink" size="md" className="flex-1" loading={busy} onClick={submit} data-testid="admin-grant-submit">Grant</Button>
        </div>
      </div>
      <style>{`
        .wt-input { width:100%; background: var(--base); border:1px solid var(--line); border-radius:0.5rem; padding:.5rem .75rem; font-size:.875rem; color: var(--ink); }
        .wt-input:focus { border-color: var(--pink); outline:none; }
      `}</style>
    </div>
  );
}

/* ---------------------------------------------------------- utilities -- */

function splitCsv(s) {
  const parts = String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
  return parts.length > 0 ? parts : null;
}

function toLocalDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDT(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function mergeIntoForm(bonus) {
  return {
    slug: bonus.slug,
    name: bonus.name,
    description: bonus.description || "",
    bonus_type: bonus.bonus_type,
    enabled: !!bonus.enabled,
    config: { ...EMPTY_FORM.config, ...(bonus.config || {}) },
    targeting: {
      ...EMPTY_FORM.targeting,
      ...(bonus.targeting || {}),
      geo_blocklist: (bonus.targeting || {}).geo_blocklist || [],
    },
    wagering: { ...EMPTY_FORM.wagering, ...(bonus.wagering || {}), game_weights: { ...EMPTY_FORM.wagering.game_weights, ...((bonus.wagering || {}).game_weights || {}) } },
  };
}

function buildPayload(form, isNew) {
  // On PATCH we only send fields that likely changed. Simpler: send the
  // partial shape the API accepts (BonusUpdate) — which excludes slug/type.
  const payload = {
    name: form.name,
    description: form.description,
    enabled: form.enabled,
    config: pickConfigForType(form),
    targeting: {
      currencies: (form.targeting.currencies || []).length > 0 ? form.targeting.currencies : null,
      geo_allowlist: (form.targeting.geo_allowlist || []).length > 0 ? form.targeting.geo_allowlist : null,
      geo_blocklist: form.targeting.geo_blocklist || [],
      user_segment: form.targeting.user_segment || "all",
      start_at: form.targeting.start_at || new Date().toISOString(),
      end_at: form.targeting.end_at || null,
      promo_code: form.targeting.promo_code || null,
    },
    wagering: {
      multiplier: Number(form.wagering.multiplier || 0),
      max_bet: form.wagering.max_bet || {},
      expiry_days: Number(form.wagering.expiry_days || 7),
      game_weights: form.wagering.game_weights || {},
    },
  };
  if (isNew) {
    payload.slug = form.slug;
    payload.bonus_type = form.bonus_type;
  }
  return payload;
}

function pickConfigForType(form) {
  const t = form.bonus_type;
  const c = form.config || {};
  if (t === "deposit_match") {
    return {
      match_percent: Number(c.match_percent || 0),
      max_bonus: c.max_bonus || {},
      min_deposit: c.min_deposit || {},
      deposit_method_whitelist: (c.deposit_method_whitelist || []).length ? c.deposit_method_whitelist : null,
    };
  }
  if (t === "cashback") {
    return {
      cashback_percent: Number(c.cashback_percent || 0),
      cycle: c.cycle || "daily",
      min_net_loss: c.min_net_loss || {},
      max_cashback: c.max_cashback || {},
    };
  }
  return {
    free_credit: c.free_credit || {},
    eligible_game_slugs: c.eligible_game_slugs || [],
    one_claim_per_user: !!c.one_claim_per_user,
  };
}
